import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const { t, i18n } = useTranslation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { key: 'nav_solutions', path: '/solutions' },
    { key: 'nav_infrastructure', path: '/infrastructure' },
    { key: 'nav_security', path: '/security' },
    { key: 'nav_cases', path: '/case-studies' },
    { key: 'nav_about', path: '/about' },
  ];

  const languages = [
    { code: 'en', label: 'EN' },
    { code: 'zh-TW', label: '繁中' },
  ];

  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-400 ${
        isScrolled ? 'bg-slate-900/98 backdrop-blur-sm border-b border-slate-800' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5 cursor-pointer">
            <div className="flex items-end gap-1">
              <div
                className="relative flex items-baseline leading-none select-none"
                style={{ fontFamily: "'Comfortaa', cursive" }}
              >
                <span className="relative text-[36px] font-bold text-white leading-none">
                  <span
                    className="absolute text-[12px] font-bold text-cyan-400 leading-none"
                    style={{ top: '-7px', left: '50%', transform: 'translateX(-50%)' }}
                  >
                    °
                  </span>
                  a
                </span>
                <span className="text-[36px] font-bold text-white leading-none">b</span>
              </div>
              <div className="flex flex-col leading-none ml-1.5 pb-[1px]">
                <span className="text-white font-bold text-sm tracking-wide">A<span className="text-cyan-400">&amp;</span>B</span>
                <span className="text-slate-400 text-xs tracking-widest font-medium">SYSTEM</span>
              </div>
            </div>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-8">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  location.pathname === item.path ? 'text-cyan-400' : 'text-slate-300 hover:text-white'
                }`}
              >
                {t(item.key)}
              </Link>
            ))}
          </div>

          {/* Right: Language Switcher + CTA */}
          <div className="hidden lg:flex items-center gap-3">
            {/* Language Switcher */}
            <div className="flex items-center bg-slate-800/60 border border-slate-700 rounded-lg overflow-hidden">
              {languages.map((lang, idx) => (
                <button
                  key={lang.code}
                  onClick={() => i18n.changeLanguage(lang.code)}
                  className={`px-3 py-1.5 text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                    i18n.language === lang.code
                      ? 'bg-cyan-500 text-slate-900'
                      : 'text-slate-400 hover:text-white'
                  } ${idx !== 0 ? 'border-l border-slate-700' : ''}`}
                >
                  {lang.label}
                </button>
              ))}
            </div>
            <Link
              to="/contact"
              className="bg-cyan-500 hover:bg-cyan-400 text-slate-900 font-semibold text-sm px-5 py-2.5 rounded-lg transition-all whitespace-nowrap cursor-pointer"
            >
              {t('nav_contact_cta')}
            </Link>
          </div>

          {/* Mobile toggle */}
          <button
            className="lg:hidden w-10 h-10 flex items-center justify-center text-white cursor-pointer"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            <i className={`text-2xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-3-line'}`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileOpen && (
          <div className="lg:hidden bg-slate-900 border-t border-slate-800 px-6 py-4">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setMobileOpen(false)}
                className="block py-3 text-slate-300 hover:text-cyan-400 text-sm font-medium border-b border-slate-800 cursor-pointer"
              >
                {t(item.key)}
              </Link>
            ))}
            {/* Mobile Language Switcher */}
            <div className="flex items-center gap-2 mt-4 mb-2">
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => { i18n.changeLanguage(lang.code); setMobileOpen(false); }}
                  className={`px-4 py-2 rounded-lg text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                    i18n.language === lang.code
                      ? 'bg-cyan-500 text-slate-900'
                      : 'bg-slate-800 text-slate-400 border border-slate-700'
                  }`}
                >
                  {lang.label}
                </button>
              ))}
            </div>
            <Link
              to="/contact"
              onClick={() => setMobileOpen(false)}
              className="block text-center bg-cyan-500 text-slate-900 font-semibold text-sm px-5 py-3 rounded-lg mt-2 cursor-pointer whitespace-nowrap"
            >
              {t('nav_contact_cta')}
            </Link>
          </div>
        )}
      </nav>
    </>
  );
}
